// Copyright (c) 2021 WiseTime. All rights reserved.

import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  SimpleInterpolation,
  ThemeProps,
} from "styled-components/macro"
import SvgIcon from "features/shared/SvgIcon"
import {
  ActiveListItem,
  IconButton,
  IconButtonActive,
  ListItemDescription,
  ListItemLabel,
  SharedDropdownContent,
} from "../styles"
import { ActionPaneHeader } from "features/shared/styles"
import { darken } from "polished"
import { isIosWiseTime } from "../../../app/config"

export const DropdownHeader = styled.button<{
  isDropdownVisible: boolean,
}>`
  ${IconButton};
  outline: 0;
  
  ${({ isDropdownVisible }) => {
    if (!isDropdownVisible) {
      return css`
        &:hover {
          ${IconButtonActive};
        }
      `
    }
  }}
  
  ${({ isDropdownVisible, theme }) => {
    if (isDropdownVisible) {
      return css`
        ${IconButtonActive};
      `
    }
    return css`
      color: ${theme.colors.dimGray};
    `
  }}
`

export const Wrapper = styled.div`
  cursor: initial;
`

export const DropdownContent = styled.div`
  ${SharedDropdownContent};
  border: 1px solid rgba(193, 193, 193, .5);
  display: flex;
  flex-direction: column;
  z-index: 105;
  width: 100%;
  max-width: 450px;
  min-height: 338px;
  max-height: 550px;
  
  ${ActionPaneHeader} & {
    top: 56px;
    
    @media(max-width: ${({ theme }): string => theme.screen.xs}) {
      right: auto;
      left: 0;
    }
  }
`

export const Input = styled.input`
  color: ${({ theme }): string => theme.colors.charcoal};
  font-size: 1.4rem;
  width: 100%;
  height: ${({ theme }): string => theme.searchDropdownInputHeight}px;
  padding: 0 44px 0 12px;
  border-radius: 0;
  border-width: 0;
  
  &:focus {
    outline: none;
  }
`

export const DropdownHeaderIcon = styled(SvgIcon)`
  width: ${({ theme }) => theme.iconSizeInButton};
  height: ${({ theme }) => theme.iconSizeInButton};
`

export const ListsWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  box-shadow: 0 -1px 0 ${({ theme }): string => theme.colors.gainsboro};
`

export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 50%;
  &:not(:first-child) {
    border-left: 1px solid ${({ theme }): string => theme.colors.gainsboro};
  }
`

export const SearchResults = styled(ListsWrapper)`
  ${Column} {
    width: 100%;
  }
`

export const ListHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  color: ${({ theme }): string => theme.colors.dimGray};
  font-size: 1.1rem;
  line-height: 14px;
  font-weight: 600;
  height: ${({ theme }): string => theme.searchDropdownListHeaderHeight}px;
  padding: 0 12px 0 9px;
  background-color: ${({ theme }): string => theme.colors.gallery};
  border-left: 3px solid transparent;

  svg {
    margin-right: 4px;
  }
`

export const RecentListHeader = styled(ListHeader)`
  justify-content: space-between;
`

export const ListHeaderPrimary = styled(ListHeader)`
  justify-content: space-between;
  color: ${({ theme }): string => theme.colors.indigo};
  background-color: ${({ theme }): string => theme.colors.zircon};
  border-color: ${({ theme }): string => theme.colors.indigo};
`

export const List = styled.ul`
  flex: 1;
  overflow-y: auto;
  margin: 0;
`

export const StyledFavouritedIcon = styled(SvgIcon)`
  width: 12px;
  min-width: 12px;
  height: 11px;
`

export const StyledFavouriteIcon = styled(SvgIcon)`
  height: 11px;
`

export const StyledUnfavouriteIcon = styled(SvgIcon)`
  height: 12px;
`

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
`

export const DraggableHandler = styled.span`
  cursor: row-resize;
  opacity: 0;
  width: 11px;
  height: 11px;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -4px;
  transition: opacity .3s ease;
  
  &:focus {
    outline: none;
  }

  &:after {
    content: "";
    width: 1px;
    height: 1px;
    display: inline-block;
    position: absolute;
    top: 0;
    left: 4px;

  ${({ theme }): SimpleInterpolation => {
    return css`
      background-color: ${theme.colors.indigo};
      box-shadow: 2px 0 ${theme.colors.indigo},
                  0 2px ${theme.colors.indigo},
                  2px 2px ${theme.colors.indigo},
                  0 4px ${theme.colors.indigo},
                  2px 4px ${theme.colors.indigo},
                  0 6px ${theme.colors.indigo},
                  2px 6px ${theme.colors.indigo};
    `
  }}
  }
`

export const ListItemButton = styled.button`
  opacity: 0;
  color: ${({ theme }): string => theme.colors.indigo};
  padding: 2px 5px;
  position: absolute;
  right: 5px;
  top: 50%;
  margin-top: -10px;
  transition: opacity .3s ease, color .3s ease;

  ${StyledFavouriteIcon} {
    margin-top: -1px;
  }
  
  svg {
    width: 12px;
    min-width: 12px;
    fill: currentColor;
    vertical-align: middle;
  }
  
  &:hover {
    color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
  }
`

export const ListItem = styled.li<{ isAdded?: boolean, isActive: boolean }>`
  background-color: ${({ theme }): string => theme.colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  padding: .5rem 3.2rem .5rem 1.2rem;

  &:focus {
    outline: none;
  }
  
  b {
    color: #2c98e0;
  }
  
  ${({ isAdded, theme }): SimpleInterpolation => {
    if (isAdded) {
      return css`
        cursor: default;
        
        ${ListItemLabel} {
          color: ${theme.colors.silverChalice};
        }
        
        ${ListItemDescription} {
          color: ${theme.colors.silverChalice};
        }
      `
    }
  }} 
  
  ${({ isActive }): string | FlattenInterpolation<ThemeProps<DefaultTheme>> => isActive ? ActiveListItem : ""};
  
  ${({ isAdded }) => !isIosWiseTime && css`
    &:hover {
      padding-right: 3.2rem;

      ${!isAdded && ActiveListItem};

      ${DraggableHandler},
      ${ListItemButton} {
        opacity: 1;
      }
    }
  `};
  
  ${({ isActive, theme }): SimpleInterpolation => {
    if (!isActive) {
      return css`
        &:not(:hover) {
          &:nth-child(2n) {
            background-color: ${theme.colors.alabaster};
          }
        }
      `
    }
    return ""
  }}
`

export const ListItemDraggable = styled(ListItem)<{
  isDragging?: boolean,
  isActive?: boolean,
}>`
  ${({ isDragging }): SimpleInterpolation => {
    if (isDragging) {
      return css`
        pointer-events: none;
      `
    }
    return ""
  }}
  &.dragging-helper-class {
    pointer-events: auto !important;
    cursor: row-resize;
    background-color: ${({ theme }): string => theme.colors.white};
    padding-right: .5rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, .3);
    z-index: 108;
    
    ${ListItemButton} {
      opacity: 0;
    }
    
    ${ListItemLabel},
    ${ListItemDescription} {
      color: ${({ theme }): string => theme.colors.mortar};
    }
  }
  ${({ isActive }): string | FlattenInterpolation<ThemeProps<DefaultTheme>> => isActive ? ActiveListItem : ""};
`
